import { Tearsheet } from '@carbon/ibm-products';
import { Loading } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useCreateParishBookPersonMutation } from 'api/parishBookEndpoints';
import {
    CONTACT_PERSON_DESCRIPTION_MAX_LENGTH,
    CONTACT_PERSON_TEXT_MAX_LENGTH,
    isEmailValid,
    isPhoneNumberValid,
} from 'utils/validationUtils';
import { ParishBookPersonProvider, useParishBookPersonContext } from '../ParishBookPersonContext';
import ParishBookPersonForm from '../ParishBookPersonForm/ParishBookPersonForm';

import styles from './AddParishBookPersonView.module.scss';

const defaultState = {
    id: '',
    categoryId: '',
    firstName: '',
    lastName: '',
    availability: '',
    description: '',
    phoneNumber: '',
    email: '',
    visibleForGuests: false,
};

type Props = {
    open?: boolean;
    onClose?: () => void;
}

const AddParishBookPersonViewContainer = ({
    open = true,
    onClose,
}: Props) => (
    <ParishBookPersonProvider initialState={defaultState}>
        <AddParishBookPersonView open={open} onClose={onClose} />
    </ParishBookPersonProvider>
);

const AddParishBookPersonView = ({
    open = true,
    onClose,
}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const currentEntityId = useGetCurrentEntityId();
    const { values } = useParishBookPersonContext();
    const [createPerson, { isLoading }] = useCreateParishBookPersonMutation();
    const [errorMessage, setErrorMessage] = useState<string>('');

    const isSubmitDisabled =
        !values.categoryId ||
        !values.firstName.trim() ||
        !values.lastName.trim() ||
        values.firstName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ||
        values.lastName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ||
        (values.availability ?? '').trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ||
        (values.description ?? '').trim().length > CONTACT_PERSON_DESCRIPTION_MAX_LENGTH ||
        !isEmailValid(values.email ?? '') ||
        !isPhoneNumberValid(values.phoneNumber ?? '');

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }

        navigate('/parish-book');
    };

    const handleSubmit = async () => {
        if (!currentEntityId) return Promise.reject(new Error('Missing entity id'));

        if (isSubmitDisabled) {
            setErrorMessage(intl.formatMessage(messages.requiredFields));
            return Promise.reject(new Error('Required fields missing'));
        }

        setErrorMessage('');

        try {
            await createPerson({
                entityId: currentEntityId,
                data: {
                    categoryId: values.categoryId,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    availability: values.availability || undefined,
                    description: values.description || undefined,
                    phoneNumber: values.phoneNumber || undefined,
                    email: values.email || undefined,
                    visibleForGuests: values.visibleForGuests || false,
                },
            }).unwrap();
            handleClose();
            return Promise.resolve();
        } catch (error) {
            setErrorMessage(intl.formatMessage(messages.submitError));
            return Promise.reject(error instanceof Error ? error : new Error('Create parish book person failed'));
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={intl.formatMessage(sharedMessages.addPerson)}
                description={intl.formatMessage(messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={handleClose}
                selectorPrimaryFocus="#parish-person-firstName"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: handleClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled,
                        loading: isLoading,
                        onClick: handleSubmit,
                    },
                ]}
            >
                {open && (
                    <>
                        <div className={styles.form}>
                            {errorMessage && (
                                <div className={styles.error}>{errorMessage}</div>
                            )}
                            <ParishBookPersonForm
                                onSubmit={handleSubmit}
                                isLoading={isLoading}
                                isSubmitDisabled={isSubmitDisabled}
                                hideActions
                            />
                        </div>
                        {isLoading && <Loading />}
                    </>
                )}
            </Tearsheet>
        </>
    );
};

const messages = defineMessages({
    description: {
        id: 'parishBook.addPerson.description',
        defaultMessage: 'Wypełnij dane osoby, którą chcesz dodać do zespołu parafii.',
    },
    requiredFields: {
        id: 'parishBook.addPerson.requiredFields',
        defaultMessage: 'Uzupełnij wymagane pola: imię, nazwisko i funkcję.',
    },
    submitError: {
        id: 'parishBook.addPerson.submitError',
        defaultMessage: 'Nie udało się dodać rekordu. Sprawdź dane i spróbuj ponownie.',
    },
});

export default AddParishBookPersonViewContainer;
